import { Box, Stack, styled, Theme, useTheme } from '@mui/material'
import { Icon as IconType } from 'react-feather'
import { SIDEBAR_WIDTH } from './constants'
import { Icon, tokens, TOOLBAR_HEIGHT, Typography } from 'ComponentLibrary'
import { pxToRemString } from 'ComponentLibrary/src/styles'
import { Link, useLocation } from 'react-router-dom'
import { CSSProperties, isValidElement, ReactNode } from 'react'

type SidebarRoute = {
    icon: ReactNode | IconType
    label: string
    slug: string
    disabled?: boolean
}
type SidebarProps = {
    elements: Record<string, SidebarRoute[]>
    style?: CSSProperties
}

const getSidebarRouteColor = (theme: Theme, isActive: boolean, disabled?: boolean) => {
    if (disabled) {
        return theme.palette.customtext.black23
    }
    if (isActive) {
        return theme.palette.primary2.green100
    }
    return theme.palette.primary1.blue100
}

export const Sidebar = ({ elements, style }: SidebarProps) => {
    const theme = useTheme()
    const location = useLocation()
    const currentLocation = location.pathname
    const basePath = currentLocation.substring(0, location.pathname.lastIndexOf('/') + 1)
    const sidebarElements = Object.entries(elements)
    return (
        <Box
            sx={{
                paddingLeft: theme.spacing(6),
                width: SIDEBAR_WIDTH,
                background: 'white',
                minHeight: `calc(100vh - ${TOOLBAR_HEIGHT} - 1px)`,
                marginBottom: '-120px',
                borderRight: '2px solid #F2F2F2',
                ...style,
            }}
        >
            {sidebarElements.map(([title, routes]) => {
                return (
                    <Stack direction="column" key="title" position="sticky" top={tokens.SizingSpacing300}>
                        <Typography
                            variant="captionSmall"
                            color={theme.palette.primary1.blue100}
                            style={{ marginBottom: theme.spacing(5) }}
                        >
                            {title}
                        </Typography>
                        {routes.map((route) => {
                            const isActive = currentLocation.endsWith(route.slug)
                            const color = route.disabled
                                ? theme.palette.customtext.black23
                                : isActive
                                  ? theme.palette.primary2.green100
                                  : theme.palette.primary1.blue100
                            return (
                                <Link
                                    to={`${basePath}${route.slug}`}
                                    style={{ textDecoration: 'none', pointerEvents: route.disabled ? 'none' : undefined, width: '100%' }}
                                    key={route.slug}
                                >
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        marginBottom={theme.spacing(6)}
                                        sx={{ '& svg': { color: getSidebarRouteColor(theme, isActive, route.disabled) } }}
                                    >
                                        {isValidElement(route.icon) ? (
                                            route.icon
                                        ) : (
                                            <Icon
                                                icon={route.icon as IconType}
                                                color={getSidebarRouteColor(theme, isActive, route.disabled)}
                                                size={18}
                                            />
                                        )}
                                        <RouteText color={color}>{route.label}</RouteText>
                                    </Stack>
                                </Link>
                            )
                        })}
                    </Stack>
                )
            })}
        </Box>
    )
}

const RouteText = styled(Typography)`
    font-size: ${pxToRemString(11)};
    font-weight: 500;
    overflow: hidden;
    padding: 0 2px;
    text-overflow: ellipsis;
    max-width: 100%;
    margin-left: 8px;
`
